import React, { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "sonner";
import {
  BookOpen, Copy, CheckCircle, Warning, Lightning, Code,
  Plug, Key, ArrowRight,
} from "@phosphor-icons/react";

const CopyBtn = ({ text, label = "Copy", testid }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setOk(true);
          toast.success("Copied to clipboard");
          setTimeout(() => setOk(false), 1500);
        } catch { toast.error("Copy failed"); }
      }}
      className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-white/10 text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
    >
      {ok ? <CheckCircle size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {ok ? "Copied" : label}
    </button>
  );
};

const CodeBlock = ({ children, testid }) => (
  <div className="relative group">
    <pre
      data-testid={testid}
      className="bg-[#0B0F19] border border-white/10 rounded-md p-4 text-[12px] leading-relaxed font-mono text-gray-300 overflow-x-auto whitespace-pre-wrap break-words"
    >{children}</pre>
    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <CopyBtn text={children} />
    </div>
  </div>
);

const Section = ({ id, title, subtitle, icon: Icon, children }) => (
  <div id={id} className="bg-[#111827] border border-white/10 rounded-lg p-6 mb-5 scroll-mt-24">
    <div className="flex items-start gap-3 mb-4">
      {Icon && <Icon size={22} weight="duotone" className="text-blue-400 mt-0.5" />}
      <div>
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="text-sm text-gray-300 space-y-3">{children}</div>
  </div>
);

const CLIENT_REGISTERED_SAMPLE = JSON.stringify({
  event: "client.registered",
  timestamp: "2026-08-01T10:15:00Z",
  data: {
    account_number: "12345678",
    full_name: "Ahmad Zulkarnain",
    email: "ahmad@example.com",
    phone: "+60123456789",
    country: "MY",
    deposit_amount: 500.0,
    registered_at: "2026-08-01T10:14:33Z"
  }
}, null, 2);

const TRADE_CLOSED_SAMPLE = JSON.stringify({
  event: "trade.closed",
  timestamp: "2026-08-05T14:22:11Z",
  data: {
    account_number: "12345678",
    ticket: "T-9876543",
    symbol: "XAGUSD",
    lot_volume: 1.5,
    open_time: "2026-08-05T13:45:00Z",
    close_time: "2026-08-05T14:22:00Z",
    profit: 12.4,
    status: "closed"
  }
}, null, 2);

const BATCH_SAMPLE = JSON.stringify({
  event: "trade.closed",
  timestamp: "2026-08-05T14:30:00Z",
  data: [
    { account_number: "12345678", ticket: "T-1", symbol: "XAGUSD", lot_volume: 1.0, close_time: "2026-08-05T14:00:00Z", profit: 5.0, status: "closed" },
    { account_number: "12345678", ticket: "T-2", symbol: "XAGUSD", lot_volume: 0.5, close_time: "2026-08-05T14:15:00Z", profit: 3.2, status: "closed" }
  ]
}, null, 2);

const SUCCESS_RESPONSE = JSON.stringify({ received: true, event: "trade.closed", matched: 1, skipped: 0 }, null, 2);

export default function BrokerDocs() {
  const [integrations, setIntegrations] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/broker-integrations");
        const webhooks = data.filter((i) => i.integration_mode === "webhook" || i.integration_mode === "both");
        setIntegrations(webhooks);
        if (webhooks.length) setSelected(webhooks[0]);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const webhookBase = `${API_BASE}/webhooks/broker`;
  const webhookUrl = selected?.webhook_token
    ? `${webhookBase}/${selected.webhook_token}`
    : `${webhookBase}/{webhook_token}`;

  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '${TRADE_CLOSED_SAMPLE.replace(/\n\s+/g, " ").replace(/\s+/g, " ")}'`;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <PageHeader
        title="Broker Webhook Integration"
        subtitle="Everything a broker's engineering team needs to push registrations and trades into MBE."
        breadcrumbs="Docs / Broker Webhook"
        testId="broker-docs-page"
      />

      {/* Quick nav */}
      <div className="flex flex-wrap gap-2 mb-6 text-xs">
        {[
          ["overview", "Overview"],
          ["endpoint", "Endpoint"],
          ["auth", "Authentication"],
          ["client-registered", "client.registered"],
          ["trade-closed", "trade.closed"],
          ["mapping", "Field Mapping"],
          ["responses", "Responses"],
          ["testing", "Testing"],
        ].map(([h, l]) => (
          <a
            key={h}
            href={`#${h}`}
            className="px-3 py-1.5 rounded-full border border-white/10 text-gray-400 hover:text-white hover:border-blue-500/40 transition-colors"
          >
            {l}
          </a>
        ))}
      </div>

      {/* Overview */}
      <Section id="overview" title="Overview" subtitle="How the integration works end-to-end." icon={BookOpen}>
        <p>
          MBE runs a <strong className="text-white">push-based webhook</strong>. Your broker platform sends
          real-time HTTP POST requests to a tokenized URL every time a client registers or closes a trade.
          MBE stores, validates, deduplicates and awards milestones automatically.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {[
            { icon: Lightning, title: "Real-time", body: "No polling. Trades hit the dashboard within seconds of your close event." },
            { icon: Key, title: "Tokenized", body: "Each integration has a unique 64-char token in the URL. No API key header required." },
            { icon: Plug, title: "Idempotent", body: "Duplicate tickets are silently rejected. Safe to retry on network failure." },
          ].map((f, i) => (
            <div key={i} className="p-3 rounded-md bg-[#0B0F19] border border-white/5">
              <f.icon size={18} className="text-blue-400 mb-2" weight="duotone" />
              <p className="font-semibold text-white text-sm">{f.title}</p>
              <p className="text-xs text-gray-400 mt-1">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Endpoint */}
      <Section id="endpoint" title="Endpoint" subtitle="Your dedicated webhook URL." icon={Plug}>
        {integrations.length > 0 && (
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Select integration</label>
            <select
              data-testid="broker-docs-integration-select"
              value={selected?.id || ""}
              onChange={(e) => setSelected(integrations.find((i) => i.id === e.target.value) || null)}
              className="bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
            >
              {integrations.map((i) => (
                <option key={i.id} value={i.id}>{i.name} · {i.integration_mode}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="flex-1 font-mono text-xs bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-blue-300 break-all">
            POST {webhookUrl}
          </div>
          <CopyBtn text={webhookUrl} testid="broker-docs-copy-url" />
        </div>
        {!selected && (
          <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-amber-500/5 border border-amber-500/30 text-amber-300 text-xs">
            <Warning size={14} className="mt-0.5" />
            <span>No webhook integration exists yet. Create one in <a href="/broker-integrations" className="underline">Broker Integrations</a> to get a real URL.</span>
          </div>
        )}
      </Section>

      {/* Auth */}
      <Section id="auth" title="Authentication" subtitle="Token-in-URL. No headers to configure." icon={Key}>
        <p>The webhook token is part of the URL path. Anyone who can call the URL is authorised to push events, so treat it like a secret.</p>
        <ul className="list-disc pl-6 text-gray-400 text-sm space-y-1">
          <li>Rotate the token from <span className="text-white">Broker Integrations → Rotate token</span> if compromised.</li>
          <li>Only allow-listed source IPs are recommended on your side.</li>
          <li>All webhook requests must use <span className="text-white font-mono">Content-Type: application/json</span>.</li>
        </ul>
      </Section>

      {/* client.registered */}
      <Section id="client-registered" title="Event · client.registered" subtitle="Send when a new client opens an account under MBE's IB code." icon={Code}>
        <p>Auto-registers the participant with status <span className="font-mono text-emerald-400">approved</span> and sets their eligibility start date.</p>
        <p className="text-xs text-gray-500">The <code className="text-white">registered_at</code> field is clamped to the campaign window if it falls outside.</p>
        <CodeBlock testid="broker-docs-client-registered-payload">{CLIENT_REGISTERED_SAMPLE}</CodeBlock>
        <div className="text-xs text-gray-500 mt-2">
          <span className="text-gray-400 font-semibold">Required fields:</span> <code>account_number</code>, <code>full_name</code>, <code>email</code>. Everything else is optional.
        </div>
      </Section>

      {/* trade.closed */}
      <Section id="trade-closed" title="Event · trade.closed" subtitle="Send when a trade is closed. Single trade or batched array both supported." icon={Code}>
        <p className="font-semibold text-white text-sm mt-2">Single trade</p>
        <CodeBlock testid="broker-docs-trade-closed-payload">{TRADE_CLOSED_SAMPLE}</CodeBlock>
        <p className="font-semibold text-white text-sm mt-4">Batched (recommended for daily flushes)</p>
        <CodeBlock testid="broker-docs-batch-payload">{BATCH_SAMPLE}</CodeBlock>
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          <p><span className="text-gray-400 font-semibold">Required fields:</span> <code>account_number</code>, <code>ticket</code>, <code>lot_volume</code>, <code>close_time</code>, <code>status</code>.</p>
          <p><span className="text-gray-400 font-semibold">Symbol filter:</span> only symbols matching campaign keywords (e.g. XAG, SILVER) count toward milestones. Other symbols are stored for behavior analytics.</p>
          <p><span className="text-gray-400 font-semibold">Close time:</span> ISO-8601 UTC. Trades outside the campaign window are stored but excluded from milestone counts.</p>
        </div>
      </Section>

      {/* Field mapping */}
      <Section id="mapping" title="Field Mapping" subtitle="Rename your fields inside the Broker Integrations page." icon={ArrowRight}>
        <p>If your platform uses different field names (e.g. <code>login_id</code> instead of <code>account_number</code>), map them once in <a href="/broker-integrations" className="text-blue-400 hover:underline">Broker Integrations</a>. MBE will translate on the fly.</p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 uppercase tracking-wider text-[10px]">
                <th className="pb-2">MBE Field</th>
                <th className="pb-2">Type</th>
                <th className="pb-2">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300 font-mono">
              {[
                ["account_number", "string", "Broker MT4/5 login. Unique per participant."],
                ["ticket", "string", "Deal / order id. Must be unique across the campaign."],
                ["symbol", "string", "e.g. XAGUSD, SILVER. Case-insensitive match against campaign symbols."],
                ["lot_volume", "number", "Traded volume in standard lots."],
                ["close_time", "ISO datetime", "UTC. Used for campaign-window check."],
                ["profit", "number", "Optional. USD profit for analytics."],
                ["status", "string", "'closed' is the only value counted."],
              ].map(([f, t, d]) => (
                <tr key={f} className="border-t border-white/5">
                  <td className="py-2 text-blue-300">{f}</td>
                  <td className="py-2 text-gray-500">{t}</td>
                  <td className="py-2 text-gray-400 font-sans">{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Responses */}
      <Section id="responses" title="Responses & Errors" subtitle="Standard HTTP + JSON. Retry on 5xx." icon={CheckCircle}>
        <p className="font-semibold text-white text-sm">Success (200)</p>
        <CodeBlock>{SUCCESS_RESPONSE}</CodeBlock>
        <p className="font-semibold text-white text-sm mt-4">Error responses</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 uppercase tracking-wider text-[10px]">
                <th className="pb-2">Code</th>
                <th className="pb-2">Meaning</th>
                <th className="pb-2">Retry?</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {[
                ["400", "Malformed JSON or missing required fields", "No — fix payload"],
                ["401", "Unknown webhook token", "No — check URL"],
                ["409", "Duplicate ticket (already ingested)", "No — safe to ignore"],
                ["429", "Rate limit exceeded", "Yes — backoff"],
                ["5xx", "Server error", "Yes — exponential backoff"],
              ].map(([c, m, r]) => (
                <tr key={c} className="border-t border-white/5">
                  <td className="py-2 font-mono text-red-300">{c}</td>
                  <td className="py-2">{m}</td>
                  <td className="py-2 text-gray-400">{r}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Testing */}
      <Section id="testing" title="Testing" subtitle="Verify your integration with a single curl." icon={Lightning}>
        <p>Copy the curl below to smoke-test your webhook URL. Update <code>account_number</code> to a real participant in your broker system.</p>
        <CodeBlock testid="broker-docs-curl">{curlExample}</CodeBlock>
        <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-blue-500/5 border border-blue-500/30 text-blue-300 text-xs">
          <Warning size={14} className="mt-0.5" />
          <span>Toggle <strong>Test Mode</strong> in Campaign Settings to accept trades outside the campaign window during integration testing. Those trades are flagged and excluded from milestone counts.</span>
        </div>
      </Section>
    </div>
  );
}
